import {Injectable} from '@angular/core';
import {EditorRegistryService} from '../../diagram-render/mx-graph/editor/editor-registry.service';
import {
  DiagramJsonHandlerService,
  IActJson,
  IFlowlineJson,
  IImageJson,
  IStickerJson,
  ITextJson,
  Position
} from '../../diagram-data/diagram-json-handler.service';
import {LocalStorageService} from './local-storage.service';
import {
  isActivity,
  isFlowline,
  isImage,
  isSticker,
  isText
} from '../../diagram-render/helpers/shapes-detectors.util';
import {NavigationService} from '../navigation/navigation.service';
import {SpinnerService} from '../spinner/spinner.service';
import * as _ from 'lodash';
import {GeneralCommands} from '../../commands/general/general.commands';
import {UndoService} from '../../commands/undo.service';
import {PopupManagerService} from './popup-manager.service';
import {MapService} from '../map/map.service';
import {DiagramTypeConfigService} from './diagram-type-config.service';
import {CanvasElementType} from '../../diagram-render/styles/diagram-type-styles.model';

declare const mxUtils: any;
declare const mxPoint: any;

interface ExtendedICopyData {
  copyData: ICopyData;
  offsetX?: number;
  offsetY?: number;
}

@Injectable({
  providedIn: 'root'
})
export class CopyCellsService {
  public static readonly STORAGE_DATA_KEY = 'copiedInfo';

  public static readonly PASTE_STEP_SIZE = 10;

  private pasteCounter = 0;

  constructor(
    private registry: EditorRegistryService,
    private jsonHandler: DiagramJsonHandlerService,
    private popupSvc: PopupManagerService,
    private navSvc: NavigationService,
    private spinner: SpinnerService,
    private commands: GeneralCommands,
    private undoSvc: UndoService,
    private mapService: MapService,
    private diagramTypeConfigService: DiagramTypeConfigService
  ) {}

  public resetPasteCounter() {
    this.pasteCounter = 0;
  }

  public copy() {
    if (!this.registry.getGraph().isEnabled()) {
      return;
    }

    try {
      LocalStorageService.set(
        CopyCellsService.STORAGE_DATA_KEY,
        JSON.stringify(this.copySelectedVertexJson())
      );
    } catch (e) {
      LocalStorageService.remove(CopyCellsService.STORAGE_DATA_KEY);
      console.error('Error while copying: ', e);
    }
  }

  public paste(event?) {
    const graph = this.registry.getGraph();

    graph.popupMenuHandler.hideMenu();
    const copyData: ICopyData = LocalStorageService.get(CopyCellsService.STORAGE_DATA_KEY);

    if (
      !copyData ||
      !graph.isEnabled() ||
      !this.popupSvc.isEmptyOrOnly('map-tree') ||
      this.navSvc.isNavActive() ||
      this.spinner.spinnerState().show
    ) {
      return;
    }

    const {dx, dy} = calculateOffsets(graph, this.pasteCounter, copyData.meta.bounds, event);
    const pasteData: ICopyData = _.flow([
      this.filterDisabledContent,
      this.offsetVertexes,
      this.unlockVertexes,
      this.stripTeamData
    ]).bind(this)({copyData, offsetX: dx, offsetY: dy});

    if (dataIsEmpty(pasteData)) {
      return;
    }

    if (!event) {
      this.pasteCounter++;
    }

    if (this.pasteCounter > 24) {
      this.resetPasteCounter();
    }

    const pasteCommand = this.commands.paste(pasteData);

    this.undoSvc.executeCommand(pasteCommand);
  }

  private copySelectedVertexJson() {
    const graph = this.registry.getGraph();
    const scale = graph.getView().getScale();
    const translate = graph.getView().getTranslate();
    const json = this.jsonHandler.get();
    const cells = graph.getSelectionCells();
    const wpBuilder = new WayPointBuilder(graph, scale, translate);

    if (!cells.length) {
      return null; // works with JSON.parse
    }

    const copyInfo: ICopyData = {
      activities: [],
      flowlines: [],
      texts: [],
      stickers: [],
      images: [],
      meta: {
        teamId: json.team.id,
        bounds: graph.getBoundingBoxFromGeometry(cells)
      }
    };

    return cells
      .map(curr => ({cell: curr, json: _.cloneDeep(curr.getJson())}))
      .reduce((acc, curr) => {
        if (isActivity(curr.cell)) {
          delete curr.json.drilldowns;
          delete curr.json.imageMode;

          if (!curr.json.image) {
            delete curr.json.image; // no enumerable keys needed if false
          }

          acc.activities.push(curr.json);
        }

        if (isText(curr.cell)) {
          acc.texts.push(curr.json);
        }

        if (isSticker(curr.cell)) {
          acc.stickers.push(curr.json);
        }

        if (isImage(curr.cell)) {
          acc.images.push(curr.json);
        }

        if (isFlowline(curr.cell)) {
          const newFlJson = recalculateFlowlineGeometry(
            curr.cell,
            curr.json,
            cells,
            acc.meta,
            wpBuilder
          );

          acc.flowlines.push(newFlJson);
        }

        return acc;
      }, copyInfo);
  }

  private unlockVertexes(copyData: ICopyData): ICopyData {
    const unlock = json => {
      if (typeof json.locked !== 'undefined') {
        json.locked = false;
      }

      return json;
    };

    copyData.activities.forEach(unlock);
    // copyData.flowlines.forEach(unlock);
    copyData.images.forEach(unlock);
    copyData.stickers.forEach(unlock);
    copyData.texts.forEach(unlock);

    return copyData;
  }

  private offsetVertexes({copyData, offsetX, offsetY}: ExtendedICopyData): ICopyData {
    const setOffsetCoords = pos => setCoords(pos, offsetX, offsetY);
    const setVertexOffset = json => setOffsetCoords(json.position);

    const setEdgeJson = (json: IFlowlineJson) => {
      if (Array.isArray(json.waypoints)) {
        json.waypoints.map(setOffsetCoords);
      }

      if (json.srcPosition) {
        setOffsetCoords(json.srcPosition);
      }

      if (json.tgtPosition) {
        setOffsetCoords(json.tgtPosition);
      }
    };

    copyData.activities.forEach(setVertexOffset);
    copyData.flowlines.forEach(setEdgeJson);
    copyData.images.forEach(setVertexOffset);
    copyData.stickers.forEach(setVertexOffset);
    copyData.texts.forEach(setVertexOffset);

    return copyData;
  }

  private stripTeamData(copyData: ICopyData): ICopyData {
    const sameTeam = this.jsonHandler.isCurrentTeam(copyData.meta.teamId);
    const groupAllId = (this.jsonHandler.groupTypeAll || {}).id;
    const isPro = this.jsonHandler.isPro();

    copyData.activities.forEach(json => {
      if (!sameTeam && json.resources && json.resources.length) {
        json.position.h -= this.diagramTypeConfigService.resourceSettings(
          json.resources.length
        ).height;
        json.resources = [];
      }

      if (!sameTeam || !isPro) {
        delete json.image;
        delete json.imageUrl;
        delete json.imageMode;
      }

      if (json.attachments) {
        json.attachments = json.attachments
          .filter(item => item.type !== 'datatable' && item.type !== 'image')
          .map(item => {
            if (!sameTeam) {
              item.groups = groupAllId ? [groupAllId] : [];

              if (item.urlEntry) {
                return {
                  mxKey: item.mxKey,
                  title: item.title,
                  type: 'url',
                  value: item.value,
                  groups: groupAllId ? [groupAllId] : []
                };
              }
            }

            return item;
          });
      }

      if (json.refModelAttachments && (!sameTeam || !isPro)) {
        json.refModelAttachments = [];
      }

      if (json.orgModelAttachments && (!sameTeam || !isPro)) {
        json.orgModelAttachments = [];
      }

      if (json.requirementAttachments && !sameTeam) {
        json.requirementAttachments = [];
      }

      if (json.storyAttachments && !sameTeam) {
        json.storyAttachments = [];
      }
    });

    if (!sameTeam || !isPro) {
      copyData.images = [];
    }

    return copyData;
  }

  private filterDisabledContent(extendedCopyData: ExtendedICopyData): ExtendedICopyData {
    const {disabledCanvasElements = [], disableResources} =
      this.diagramTypeConfigService.generalSettings();

    const typeKeyMap: Record<CanvasElementType, keyof Omit<ICopyData, 'meta'>> = {
      [CanvasElementType.ACTIVITY]: 'activities',
      [CanvasElementType.FLOWLINE]: 'flowlines',
      [CanvasElementType.TEXT]: 'texts',
      [CanvasElementType.STICKY_NOTE]: 'stickers',
      [CanvasElementType.IMAGE]: 'images'
    };

    for (const type of disabledCanvasElements) {
      const key = typeKeyMap[type];

      if (key) {
        extendedCopyData.copyData[key] = [];
      }
    }

    if (disableResources) {
      extendedCopyData.copyData.activities.forEach(json => {
        json.resources = [];
      });
    }

    return extendedCopyData;
  }
}

function recalculateFlowlineGeometry(cell, json, cells, meta, wpBuilder: WayPointBuilder) {
  if (json.srcId && needsEndpointRecalc(cells, json, true)) {
    json.srcPosition = recalculateEndpointPosition(cell.source.geometry, json, true);
    delete json.srcId;
    json.srcType = 'none';
  }

  if (json.tgtId && needsEndpointRecalc(cells, json)) {
    json.tgtPosition = recalculateEndpointPosition(cell.target.geometry, json, false);
    delete json.tgtId;
    json.tgtType = 'none';
  }

  if (json.srcPosition && json.srcType === 'none') {
    recalculateBounds(json.srcPosition, meta);
  }

  if (json.tgtPosition && json.tgtType === 'none') {
    recalculateBounds(json.tgtPosition, meta);
  }

  const srcEmpty = json.srcType === 'none';
  const tgrEmpty = json.tgtType === 'none';
  const emptyWaypoints = _.isEmpty(json.waypoints);

  if (emptyWaypoints && tgrEmpty) {
    const points = wpBuilder.buildWayPoints(cell);

    json.waypoints = srcEmpty ? points : points.length ? points : null;
  }

  json = wpBuilder.normalizeFlJson(json);

  return json;
}

function needsEndpointRecalc(cells, json, isSource?) {
  const id = isSource ? json.srcId : json.tgtId;
  const type = isSource ? json.srcType : json.tgtType;
  const endpointCell = cells.find(c => Number(c.id) === Number(id));

  return type === 'connector' || type === 'terminator' || !endpointCell;
}

function recalculateEndpointPosition(geo, json, isSource?) {
  const cx = isSource
    ? json.position.connectionPoints.exitX
    : json.position.connectionPoints.entryX;
  const cy = isSource
    ? json.position.connectionPoints.exitY
    : json.position.connectionPoints.entryY;
  const dx = geo.width * cx;
  const dy = geo.height * cy;

  return {
    x: Math.round(geo.x + dx), // todo: maybe we don't need this round
    y: Math.round(geo.y + dy)
  };
}

function recalculateBounds(position: Position, meta) {
  const x = position.x;
  const y = position.y;
  const bounds: IBounds = meta.bounds;

  if (bounds) {
    if (x < bounds.x) {
      bounds.x = x;
    }

    if (x > bounds.x + bounds.width) {
      bounds.width = x - bounds.x;
    }

    if (y < bounds.y) {
      bounds.y = y;
    }

    if (y > bounds.y + bounds.height) {
      bounds.height = y - bounds.y;
    }
  } else {
    meta.bounds = {
      x,
      y,
      width: 100,
      height: 100
    };
  }
}

function createNewMxPoint(graph) {
  const scale = graph.view.scale;
  const trans = graph.view.translate;
  const container = graph.container;
  const center = {
    x: window.innerWidth / 2,
    y: window.innerHeight / 2
  };
  const point = mxUtils.convertPoint(container, center.x, center.y);

  point.x = point.x / scale - trans.x;
  point.y = point.y / scale - trans.y;

  return point;
}

function setCoords(struct: Position, offsetX?: number, offsetY?: number) {
  struct.x = Number(struct.x) + offsetX;
  struct.y = Number(struct.y) + offsetY;
}

function calculateOffsets(graph, steps, bounds?: IBounds, event?: MouseEvent) {
  if (bounds) {
    const point = event ? graph.getPointForEvent(event) : createNewMxPoint(graph);
    const stepSize = event ? 0 : CopyCellsService.PASTE_STEP_SIZE;

    return {
      dx: point.x - bounds.x - bounds.width / 2 + stepSize * steps,
      dy: point.y - bounds.y - bounds.height / 2 + stepSize * steps
    };
  } else {
    return {dx: 0, dy: 0};
  }
}

function dataIsEmpty(data: ICopyData): boolean {
  return (
    data.activities.length === 0 &&
    data.flowlines.length === 0 &&
    data.texts.length === 0 &&
    data.stickers.length === 0 &&
    data.images.length === 0
  );
}

export interface ICopyData {
  activities: Array<IActJson>;
  flowlines: Array<IFlowlineJson>;
  stickers: Array<IStickerJson>;
  images: Array<IImageJson>;
  texts: Array<ITextJson>;
  meta: {
    teamId: string;
    bounds: IBounds;
  };
}

interface IBounds extends Position {
  height: number;
  width: number;
}

class WayPointBuilder {
  constructor(
    private graph,
    private scale,
    private translate
  ) {}

  buildWayPoints(cell) {
    const state = this.graph.getView().getState(cell);
    const origin = state.origin;

    const convertPoint = absPoint => {
      const x = Math.round(absPoint.x / this.scale - this.translate.x - origin.x);
      const y = Math.round(absPoint.y / this.scale - this.translate.y - origin.y);

      return new mxPoint(x, y);
    };

    const onDifferentX = (pointA, pointB) => Math.round(pointA.x - pointB.x) !== 0;
    const onDifferentY = (pointA, pointB) => Math.round(pointA.y - pointB.y) !== 0;

    const pts = state.absolutePoints;
    let pt0 = pts[0];
    let pt1 = pts[1];

    const result = [];

    for (let i = 2; i < pts.length; i++) {
      const pt2 = pts[i];

      // add waypoints only if they are on different axis
      if (
        onDifferentX(pt0, pt1) ||
        (onDifferentX(pt1, pt2) && onDifferentY(pt0, pt1)) ||
        onDifferentY(pt1, pt2)
      ) {
        result.push(convertPoint(pt1.clone()));
      }

      pt0 = pt1;
      pt1 = pt2;
    }

    return result;
  }

  getScaleFactor() {
    switch (true) {
      case this.scale < 1 && this.scale > 0.4:
        return 1;

      case this.scale < 0.4 && this.scale > 0.2:
        return 2;

      case this.scale < 0.2 && this.scale > 0.1:
        return 3;

      case this.scale < 0.1 && this.scale > 0.01:
        return 5;

      default:
        return 1;
    }
  }

  // for correct view on all zoom's (flowline arrow is pointing in correct direction, flowline has normal angle ),
  // we need to connect source and target with nearest waypoints
  normalizeFlJson(json) {
    if (_.isEmpty(json.waypoints)) {
      return json;
    }

    const source = json.srcPosition;
    const target = json.tgtPosition;
    const [first] = json.waypoints;
    const last = json.waypoints[json.waypoints.length - 1];
    const factor = this.getScaleFactor();

    const vectorDifX = (vec1, vec2) => Math.floor(vec1.x - vec2.x);
    const vectorDifY = (vec1, vec2) => Math.floor(vec1.y - vec2.y);

    const updatePoints = (edgePoint, waypoint) => {
      const diffX = vectorDifX(edgePoint, waypoint);
      const diffY = vectorDifY(edgePoint, waypoint);

      if (_.inRange(diffX, -factor, factor)) {
        waypoint.x = edgePoint.x;
      }

      if (_.inRange(diffY, -factor, factor)) {
        waypoint.y = edgePoint.y;
      }
    };

    if (source) {
      updatePoints(source, first);
    }

    if (target) {
      updatePoints(target, last);
    }

    return json;
  }
}
// activities: [],
//   flowlines: [],
//   texts: [],
//   stickers: [],
//   images: [],
//   meta: {
//   teamId: json.team.id,
//     bounds: graph.getBoundingBoxFromGeometry(cells)
// }
